/**
 * Inline preview bytes for a filed document (DV-321).
 *
 * Two steps, and the second one is not optional: `vaultDocumentDownloadUrl` mints
 * a presigned S3 GET whose signature BINDS
 * `Content-Disposition: attachment; filename="…"` (SSB's
 * `generate_presigned_download` always passes the stored filename). Point an
 * `<iframe>` at that URL and the browser downloads the file instead of rendering
 * it — which is why the detail preview showed an empty pane. There is no
 * `disposition` argument on the field to ask for `inline`, so the bytes are
 * fetched and re-wrapped in a blob URL, which renders because the disposition
 * header is no longer in play.
 *
 * Consequences worth knowing:
 * - The whole file lands in memory. Gated by `maxBytes` (default 25 MB) so a
 *   100 MB+ multipart document doesn't wedge the tab; over the cap the caller
 *   gets `tooLarge` and should fall back to the download action.
 * - The GET is a cross-origin `fetch`, so the storage bucket MUST allow the app
 *   origin (`AllowedMethods: GET`). The local test bucket does.
 *
 * Requires an `ApolloProvider`, so only the connected routes use it; the
 * presentational `DocumentDetail` takes the resulting URL as a plain prop.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useVaultDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";

/** 25 MB — comfortably covers real documents without holding a huge blob. */
const DEFAULT_MAX_BYTES = 25 * 1024 * 1024;

export interface UseVaultDocumentPreviewArgs {
  /** `publicRefId` of the document (the presentational `VaultDocument.id`). */
  publicRefId?: string | null;
  /** Pin a specific version; omit for the document's current version. */
  ssbVersionNumber?: number | null;
  /** Skip entirely (e.g. the file type can't be rendered anyway). */
  skip?: boolean;
  /** Refuse to buffer anything larger than this. Defaults to 25 MB. */
  maxBytes?: number;
}

export interface UseVaultDocumentPreviewReturn {
  /** Blob URL to render, or undefined until it resolves (or on failure). */
  previewUrl?: string;
  loading: boolean;
  /** Set when the URL couldn't be minted or the bytes couldn't be fetched. */
  error: Error | null;
  /** The document is over `maxBytes`; offer the download action instead. */
  tooLarge: boolean;
}

export const useVaultDocumentPreview = ({
  publicRefId,
  ssbVersionNumber,
  skip = false,
  maxBytes = DEFAULT_MAX_BYTES,
}: UseVaultDocumentPreviewArgs): UseVaultDocumentPreviewReturn => {
  const [fetchDownloadUrl] = useVaultDocumentDownloadUrlLazyQuery({ fetchPolicy: "no-cache" });
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tooLarge, setTooLarge] = useState(false);
  // Revoked whenever the target changes or the component goes away, so switching
  // documents doesn't leak a blob per visit.
  const objectUrl = useRef<string | undefined>(undefined);

  const release = useCallback(() => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (skip || !publicRefId) {
      release();
      setPreviewUrl(undefined);
      setTooLarge(false);
      setError(null);
      return undefined;
    }

    // Guards the async tail: a fast document switch must not let an older
    // response overwrite the newer one's preview.
    let active = true;
    setLoading(true);
    setError(null);
    setTooLarge(false);

    void (async () => {
      try {
        const { data, error: queryError } = await fetchDownloadUrl({
          variables: {
            publicRefId,
            ssbVersionNumber: ssbVersionNumber ?? null,
          },
        });
        if (!active) return;
        if (queryError) throw queryError;
        const url = data?.vaultDocumentDownloadUrl?.url;
        if (!url) throw new Error("No download URL was returned for this document");

        const response = await fetch(url);
        if (!active) return;
        if (!response.ok) {
          throw new Error(
            `Could not load the document (${response.status} ${response.statusText})`,
          );
        }
        // Trust the header when it's there; otherwise the blob size is checked
        // below, once the body has actually been read.
        const declared = Number(response.headers.get("content-length") ?? "");
        if (Number.isFinite(declared) && declared > maxBytes) {
          if (active) setTooLarge(true);
          return;
        }

        const blob = await response.blob();
        if (!active) return;
        if (blob.size > maxBytes) {
          setTooLarge(true);
          return;
        }

        release();
        const next = URL.createObjectURL(blob);
        objectUrl.current = next;
        setPreviewUrl(next);
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setPreviewUrl(undefined);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [publicRefId, ssbVersionNumber, skip, maxBytes, fetchDownloadUrl, release]);

  // Unmount only — the effect above already revokes on target changes.
  useEffect(() => release, [release]);

  return { previewUrl, loading, error, tooLarge };
};

export interface UseVaultDocumentDownloadReturn {
  /**
   * Mint a fresh presigned URL for the document's bytes. Returns undefined when
   * the query fails, so the caller can decide how to surface it.
   */
  mintDownloadUrl: (
    publicRefId: string,
    ssbVersionNumber?: number | null,
  ) => Promise<string | undefined>;
}

/**
 * The download counterpart to `useVaultDocumentPreview`. Deliberately mints a
 * URL on demand rather than holding one: they are short-lived (15 min by
 * default), so one cached at open time is often expired by the time the user
 * clicks. The attachment disposition is what makes navigating to the URL save
 * the file instead of displaying it, so no anchor `download` attribute is needed.
 */
export const useVaultDocumentDownload = (): UseVaultDocumentDownloadReturn => {
  const [fetchDownloadUrl] = useVaultDocumentDownloadUrlLazyQuery({ fetchPolicy: "no-cache" });

  const mintDownloadUrl = useCallback(
    async (publicRefId: string, ssbVersionNumber?: number | null) => {
      const { data } = await fetchDownloadUrl({
        variables: { publicRefId, ssbVersionNumber: ssbVersionNumber ?? null },
      });
      return data?.vaultDocumentDownloadUrl?.url ?? undefined;
    },
    [fetchDownloadUrl],
  );

  return { mintDownloadUrl };
};
